import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Plus, Pencil, Trash2, ArrowLeft, Eye, Sparkles, Send, FileText, Clock, CheckCircle2, Loader2, X, CalendarClock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rtr_admin_token';
const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link', 'image'],
    ['clean']
  ]
};

const AdminBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API}/api/blog/admin/posts${params}`, { headers: authHeaders() });
      setPosts(data.data);
    } catch (err) { console.error('Failed to fetch blog posts:', err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const deletePost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/api/blog/admin/posts/${id}`, { headers: authHeaders() });
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (err) { alert('Failed to delete'); }
  };

  if (editing !== null) {
    return <PostEditor postId={editing} onBack={() => { setEditing(null); fetchPosts(); }} />;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/cars/admin')} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft size={16} /> Dashboard
            </button>
            <h1 className="text-lg font-bold">Blog Manager</h1>
          </div>
          <button onClick={() => setEditing('new')}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="new-post-btn"
          >
            <Plus size={16} /> New Post
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          {['', 'draft', 'published', 'scheduled'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
            >{s || 'All'}</button>
          ))}
          <span className="text-gray-500 text-sm ml-auto">{posts.length} posts</span>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <FileText size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-500">No blog posts yet</p>
            <button onClick={() => setEditing('new')} className="text-orange-500 hover:underline mt-2 text-sm">Create your first post</button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div key={post.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-4" data-testid={`blog-post-${post.id}`}>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{post.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span>{post.category}</span>
                    <span><Clock size={12} className="inline mr-1" />{post.reading_time} min</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${post.status === 'published' ? 'bg-green-500/10 text-green-400' : post.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                      {post.status}{post.status === 'scheduled' && post.scheduled_at ? ` ${new Date(post.scheduled_at).toLocaleDateString()}` : ''}
                    </span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {post.status === 'published' && (
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                      <Eye size={14} /> View
                    </a>
                  )}
                  <button onClick={() => setEditing(post.id)} className="flex items-center gap-1.5 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`edit-post-${post.id}`}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button onClick={() => deletePost(post.id)} className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`delete-post-${post.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const PostEditor = ({ postId, onBack }) => {
  const [form, setForm] = useState({
    title: '', content: '', excerpt: '', featured_image: '',
    category: 'General', tags: '', author: 'RunTheRays Team',
    seo_title: '', seo_description: '', status: 'draft', scheduled_at: ''
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [showAi, setShowAi] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [loading, setLoading] = useState(postId !== 'new');

  const fetchPost = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/blog/admin/posts/${postId}`, { headers: authHeaders() });
      const p = data.data;
      setForm({
        title: p.title || '', content: p.content || '', excerpt: p.excerpt || '',
        featured_image: p.featured_image || '', category: p.category || 'General',
        tags: (p.tags || []).join(', '), author: p.author || 'RunTheRays Team',
        seo_title: p.seo_title || '', seo_description: p.seo_description || '',
        status: p.status || 'draft', scheduled_at: p.scheduled_at || ''
      });
      if (p.scheduled_at) setScheduleDate(p.scheduled_at.slice(0, 16));
    } catch (err) { console.error('Failed to fetch post:', err); }
    finally { setLoading(false); }
  }, [postId]);

  useEffect(() => {
    if (postId !== 'new') fetchPost();
  }, [postId, fetchPost]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async (overrideStatus, scheduledAt = null) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        status: overrideStatus || form.status,
        scheduled_at: scheduledAt || form.scheduled_at
      };
      if (postId === 'new') {
        await axios.post(`${API}/api/blog/admin/posts`, payload, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/api/blog/admin/posts/${postId}`, payload, { headers: authHeaders() });
      }
      onBack();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    if (!aiTopic) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/blog/admin/generate`, {
        topic: aiTopic,
        keywords: aiKeywords ? aiKeywords.split(',').map(k => k.trim()) : [],
        tone: 'professional'
      }, { headers: authHeaders(), timeout: 120000 });
      const r = data.data;
      setForm(prev => ({
        ...prev,
        title: r.title || prev.title,
        content: r.content || prev.content,
        excerpt: r.excerpt || prev.excerpt,
        seo_title: r.seo_title || prev.seo_title,
        seo_description: r.seo_description || prev.seo_description,
        tags: (r.tags || []).join(', '),
        category: r.category || prev.category
      }));
      setShowAi(false);
    } catch (err) {
      alert('AI generation failed. Please try again.');
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={onBack} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAi(true)} className="flex items-center gap-1.5 bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="ai-generate-btn">
              <Sparkles size={16} /> AI Generate
            </button>
            <button onClick={() => handleSave('draft')} disabled={saving} className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="save-draft-btn">
              <FileText size={16} /> Save Draft
            </button>
            <button onClick={() => setShowSchedule(true)} disabled={saving} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="schedule-btn">
              <CalendarClock size={16} /> Schedule
            </button>
            <button onClick={() => handleSave('published')} disabled={saving} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="publish-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publish
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-4">
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Post title..." className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-xl font-bold text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
              data-testid="post-title-input" />
            <textarea value={form.excerpt} onChange={e => set('excerpt', e.target.value)} rows={2}
              placeholder="Brief excerpt (shown in listing cards)..." className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 resize-none"
              data-testid="post-excerpt-input" />
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden blog-editor">
              <ReactQuill theme="snow" value={form.content} onChange={v => set('content', v)} modules={QUILL_MODULES}
                placeholder="Write your article content here..." className="text-white" />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Post Settings</h3>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Category</label>
                <select value={form.category} onChange={e => set('category', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                  data-testid="post-category">
                  {['General', 'Supply Chain', 'Sourcing', 'Quality Control', 'Logistics', 'Trade Guide', 'Industry Insights'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Tags (comma separated)</label>
                <input type="text" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="sourcing, china, trade"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  data-testid="post-tags" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Author</label>
                <input type="text" value={form.author} onChange={e => set('author', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                  data-testid="post-author" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Featured Image URL</label>
                <input type="text" value={form.featured_image} onChange={e => set('featured_image', e.target.value)} placeholder="https://..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  data-testid="post-image" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Status</label>
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${form.status === 'published' ? 'bg-green-500/10 text-green-400' : form.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                  {form.status === 'scheduled' ? (
                    <span className="flex items-center gap-2">
                      <CalendarClock size={14} />
                      Scheduled: {form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : 'No date set'}
                    </span>
                  ) : (
                    <span className="capitalize">{form.status}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">SEO</h3>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">SEO Title</label>
                <input type="text" value={form.seo_title} onChange={e => set('seo_title', e.target.value)} placeholder="Max 60 characters"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  data-testid="post-seo-title" />
                <span className="text-xs text-gray-600">{form.seo_title.length}/60</span>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Meta Description</label>
                <textarea value={form.seo_description} onChange={e => set('seo_description', e.target.value)} rows={2} placeholder="Max 155 characters"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 resize-none"
                  data-testid="post-seo-desc" />
                <span className="text-xs text-gray-600">{form.seo_description.length}/155</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-sm p-6 relative">
            <button onClick={() => setShowSchedule(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X size={20} /></button>
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock size={20} className="text-blue-400" />
              <h2 className="text-lg font-bold">Schedule Post</h2>
            </div>
            <p className="text-gray-400 text-sm mb-4">Choose when this post should be automatically published.</p>
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Publish Date & Time</label>
                <input
                  type="datetime-local"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                  data-testid="schedule-datetime-input"
                />
              </div>
              {scheduleDate && (
                <p className="text-blue-400 text-xs">
                  Will publish on {new Date(scheduleDate).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
              <button
                onClick={() => { handleSave('scheduled', scheduleDate); setShowSchedule(false); }}
                disabled={!scheduleDate || saving}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="confirm-schedule-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <CalendarClock size={16} />}
                Schedule Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Generate Modal */}
      {showAi && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md p-6 relative">
            <button onClick={() => setShowAi(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X size={20} /></button>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={20} className="text-purple-400" />
              <h2 className="text-lg font-bold">AI Content Generator</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Topic / Title</label>
                <input type="text" value={aiTopic} onChange={e => setAiTopic(e.target.value)}
                  placeholder="e.g. How to Source Electronics from China"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  data-testid="ai-topic-input" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1 block">Target Keywords (comma separated)</label>
                <input type="text" value={aiKeywords} onChange={e => setAiKeywords(e.target.value)}
                  placeholder="e.g. sourcing from china, supplier verification"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  data-testid="ai-keywords-input" />
              </div>
              <button onClick={generateAI} disabled={generating || !aiTopic}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="ai-generate-submit"
              >
                {generating ? <><Loader2 size={16} className="animate-spin" /> Generating (30-60s)...</> : <><Sparkles size={16} /> Generate Article</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .blog-editor .ql-toolbar { background: #1f2937; border-color: #374151 !important; }
        .blog-editor .ql-toolbar button { color: #9ca3af !important; }
        .blog-editor .ql-toolbar button:hover { color: #f97316 !important; }
        .blog-editor .ql-toolbar .ql-stroke { stroke: #9ca3af !important; }
        .blog-editor .ql-toolbar button:hover .ql-stroke { stroke: #f97316 !important; }
        .blog-editor .ql-toolbar .ql-fill { fill: #9ca3af !important; }
        .blog-editor .ql-toolbar .ql-picker-label { color: #9ca3af !important; }
        .blog-editor .ql-container { border-color: #374151 !important; min-height: 400px; }
        .blog-editor .ql-editor { color: #e5e7eb; font-size: 15px; line-height: 1.7; min-height: 400px; }
        .blog-editor .ql-editor.ql-blank::before { color: #4b5563 !important; }
      `}</style>
    </div>
  );
};

export default AdminBlog;
